import { AfterViewChecked, ChangeDetectorRef, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { MessageService } from 'primeng/api';
import { forkJoin, Observable, Subject, take, takeUntil } from 'rxjs';
import { AuthRoles } from 'src/app/auth/auth.types';
import { PeopleService } from 'src/app/services/people.service';
import { AppPermissions } from 'src/app/shared/app.permissions';
import { LocationService } from 'src/app/services/location.service';
import { AuthorizationModel } from 'src/app/shared/models/authorization-model';
import { AppUtils } from '../../shared/app.utils';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'ness-person-detail',
  templateUrl: './person-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    '.col-lg-9.col-md-8 { font-weight: 700;  }'
  ]
})
export class PersonDetailComponent implements OnInit, OnDestroy, AfterViewChecked {
  canViewIRs = AppPermissions.canViewIRs;
  canViewResults = AppPermissions.canViewResults;
  canEditPerson: AuthRoles[] = AppPermissions.canEditPerson;
  canDeletePerson: AuthorizationModel = AppPermissions.canDeletePerson;
  @Input() isEventPersonForm: boolean = false;
  @Output() showPersonAddressDetail: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() onEditPerson: EventEmitter<string> = new EventEmitter<string>();
  @Output() deletePersonCompleted: EventEmitter<string> = new EventEmitter<string>();
  showDeleteButton: boolean = false;
  showDeleteHistoryForm: boolean = false;
  deleteHistory: ServerModels.ENESS_DeleteHistory;
  deleteHistoryTargetId: string;
  deleteHistoryTargetTableName: string = "ENESS_Person";
  personAddress: ServerModels.ENESS_AddressDTO;
  displaySidebar: boolean = false;
  openViewPersonAddressDetails: boolean = false;
  openViewPersonAddressDetailForm: boolean = false;
  hasPermissionToEditAddress: boolean = false;
  personAddressTypeFromRef: string;
  personAddressTypeOther: string;
  personAddressType: string;
  showLocationView: boolean = false;
  showEditLocationView: boolean = false;
  isPersonDetailFullPage: boolean = false;
  userTimeZone: string;

  private _unsubscribe$: Subject<void> = new Subject<void>();

  private _showEditButton: boolean = false;
  get showEditButton(): boolean {
    return this._showEditButton;
  }

  set showEditButton(b: boolean) {
    this._showEditButton = b;
  }

  @Input() set hasDeletePermission(hasDeletePermission: boolean) {
    this.showDeleteButton = hasDeletePermission;
  }

  private _personDTO: ServerModels.ENESS_PersonDTO = null;
  @Input() set personDTO(person: ServerModels.ENESS_PersonDTO) {
    this._personDTO = person;
  }

  get personDTO(): ServerModels.ENESS_PersonDTO {
    return this._personDTO;
  }

  @Input() set suppressEdit(b: boolean) {
    this.showEditButton = !b && (!!this._personDTO?.PersonID || !!this._personDTO?.PersonStagingID);
  }

  get isJuvenile(): boolean {
    if (!this.personDTO || !this.personDTO.DateOfBirth)
      return false;

    return Date.now() - Date.parse(this.personDTO.DateOfBirth.toString()) < 567648000000;
  }

  constructor(public peopleService: PeopleService,
    private locationService: LocationService,
    public hostElement: ElementRef,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router) { }

  ngOnInit() {
    this.userTimeZone = moment.tz.zone(moment.tz.guess()).abbr(new Date().getTimezoneOffset());

    if (this.route.snapshot.routeConfig.component.name === 'PersonDetailComponent') {
      const personId = this.route.snapshot.paramMap.get('id');
      if (!!personId) {
        forkJoin({
          person:this.peopleService.getPersonDTOById(personId),
          canDelete:this.peopleService.hasDeletePermission(personId),
          cases:this.peopleService.getPersonLECases(personId)
        }).pipe(takeUntil(this._unsubscribe$)).subscribe({
          next: (val: { person: ServerModels.ENESS_PersonDTO, canDelete: boolean, cases: ServerModels.PersonLECases[] }) => {
            if (val.person != null) {
              this._personDTO = val.person;
              this.hasDeletePermission = val.canDelete;
              this.showEditButton = !this.suppressEdit;
              this.peopleService.personLECasesSubj.next(val.cases || []);
            }
          }
        });
      }
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this._personDTO = null;
    this.messageService.clear('personDetailMsg');
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  public editPerson(): void {
    if (!this.isEventPersonForm) {
      if (this.route.snapshot.routeConfig.component.name === 'CrimeGunDetailComponent') {
        this.onEditPerson.emit(this.personDTO.PersonID);
      } else {
        this.router.navigate(['/person-form/' + this.personDTO.PersonID]);
      }
    }
    else {
      this.onEditPerson.emit(this.personDTO.PersonStagingID);
    }
  }

  public deletePerson(): void {
    this.deleteHistoryTargetId = this.personDTO.PersonID;
    this.showDeleteHistoryForm = true;
  }

  public onDeleteHistorySubmitted(data: ServerModels.ENESS_DeleteHistory): void {
    if (data != null) {
      this.showDeleteHistoryForm = false;
      this.peopleService.DeletePerson(data).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (people: ServerModels.ENESS_Person) => {
          this.deletePersonCompleted.emit(data.TargetID);
        }
      });
    }
  }

  public onCloseDeleteHistoryForm(): void {
    this.showDeleteHistoryForm = false;
  }

  public viewAddressDetails(addressID: string, addressStagingId: string, isEditable: boolean, addressType: string, addressOther: string): void {
    if (this.isPersonDetailFullPage) {
      this.openAddressDetails(addressID, isEditable, addressType, addressOther);
    } else {
      if (addressID === null) {
        this.showPersonAddressDetail.emit([addressStagingId, isEditable, addressType, addressOther, true]);
      }
      else {
        this.showPersonAddressDetail.emit([addressID, isEditable, addressType, addressOther, false]);
      }
    }
  }

  public downloadPersonAttachment(file: ServerModels.ENESS_XREF_PeopleAttachmentDTO): void {
    if (!file) {
      return;
    }

    let attachmentObservable: Observable<Blob> = this.isEventPersonForm ? this.peopleService.getAttachmentByIdStaging(file.AttachmentStagingID) : this.peopleService.getAttachmentById(file.AttachmentID);

    attachmentObservable.subscribe(
      (attachment:Blob) => AppUtils.embedDownloadLink(attachment, file.FileName),
      (err:any) => {
        this.messageService.add({ key:'personDetailMsg', severity: 'error', summary:'Attachment error:', detail:`${err} Unable to retrieve requested attachment.`});
      }
    )
  }
  
  public openRelatedCrimeGun(evt: Event, cgn: string): void {
    this.peopleService.getCrimeGunId(cgn.trim()).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (id:string) => {
        evt.preventDefault();
        const token = sessionStorage.getItem(environment.authTokenStorageKey);
        let url = (<HTMLLinkElement>evt.target).baseURI + '/crimegun/' + id;
        let windowObjRef = window.open(url, '_blank');
        windowObjRef.sessionStorage.setItem(environment.authTokenStorageKey, token);
      }
    });
  }

  public openAddressDetails(addressID: string, isEditable: boolean, addressType: string, addressOther: string): void {
    this.displaySidebar = true;
    this.openViewPersonAddressDetails = true;
    this.openViewPersonAddressDetailForm = false;
    this.hasPermissionToEditAddress = isEditable;
    this.personAddressTypeFromRef = addressType;
    this.personAddressTypeOther = addressOther;
    this.personAddressType = addressType === "Other" ? addressType + ": " + addressOther : addressType;
    forkJoin({
      address:this.locationService.getAddressDetailsById(addressID),
      leCases:this.locationService.getAddressLECases(addressID),
      people:this.locationService.getAddressPeople(addressID)
    }).pipe(take(1)).subscribe({
      next: (val: { address: ServerModels.ENESS_AddressDTO, leCases: ServerModels.AddressLECases[], people: ServerModels.AddressPeople[] }) => {
        this.showLocationView = true;
        this.personAddress = val.address;
        this.locationService.addressLECasesSubj.next(val.leCases);
        this.locationService.addressPeopleSubj.next(val.people);
      },
      error: (err:any) => {
        this.messageService.addAll([{ key: 'viewAddressDetailsMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      }
    })
  }

  public onShowSidebar(): void {
    window.setTimeout(() => {
      let targetEl = this.hostElement.nativeElement.getElementsByClassName('ui-sidebar-active');
      if (targetEl.length) {
        targetEl[0].scrollTop = 0;
        targetEl[0].setAttribute("tabindex", "-1");
        targetEl[0].focus();
      }
    }, 1000);
  }

  public onViewPersonAddressClose(): void {
    this.openViewPersonAddressDetails = false;
    this.openViewPersonAddressDetailForm = false;
    this.displaySidebar = false;
    this.showLocationView = false;
    this.showEditLocationView = false;
    this.personAddress = null;
  }

  public onLocationFormClose(newAddress: ServerModels.ENESS_AddressDTO): void {
    this.openAddressDetails(newAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther);
  }

  public editLocation(editLocation: string): void {
    if (editLocation === 'Edit') {
      this.openViewPersonAddressDetails = false;
      this.openViewPersonAddressDetailForm = true;
      this.showLocationView = false;
      this.showEditLocationView = true;
    }
  }

  public onClickCloseButton(): void {
    if (this.openViewPersonAddressDetails) {
      if (this.personAddress && this._personDTO && this._personDTO.ENESS_XREF_PeopleAddresses.length > 0) {
        let index = this._personDTO.ENESS_XREF_PeopleAddresses.findIndex(x => x.AddressID == this.personAddress.AddressID);
        this._personDTO.ENESS_XREF_PeopleAddresses[index].ENESS_Address = this.personAddress;
      }

      this.onViewPersonAddressClose();
    }
    if (this.openViewPersonAddressDetailForm) {
      this.openAddressDetails(this.personAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther);
    }
  }
}
